// Case studies. Each entry is linked to a city via citySlug.
// Slugs are ASCII, lowercase, kebab-case.

const HERO_IMAGES = {
  stockholm: "https://images.pexels.com/photos/37443358/pexels-photo-37443358.jpeg",
  goteborg: "https://images.pexels.com/photos/9455270/pexels-photo-9455270.jpeg",
  malmo: "https://images.pexels.com/photos/38034768/pexels-photo-38034768.jpeg",
  uppsala: "https://images.pexels.com/photos/9455270/pexels-photo-9455270.jpeg",
  vasteras: "https://images.pexels.com/photos/37443358/pexels-photo-37443358.jpeg",
  orebro: "https://images.pexels.com/photos/38034768/pexels-photo-38034768.jpeg",
  linkoping: "https://images.pexels.com/photos/9455270/pexels-photo-9455270.jpeg",
  helsingborg: "https://images.pexels.com/photos/37443358/pexels-photo-37443358.jpeg",
};

export const CASE_STUDIES = {
  "tasstraff-stockholm": {
    slug: "tasstraff-stockholm",
    citySlug: "stockholm",
    business: "Tassträff Hundpensionat",
    city: "Stockholm",
    area: "Bromma, Stockholm",
    industry: "Hundpensionat",
    size: "32 rum · 14 anställda",
    customerSince: "Kund sedan 2023",
    person: { name: "Maria Lindqvist", role: "Ägare", initial: "M" },
    modules: ["Hundpensionat CMS", "Fakturering", "Ägar-app"],
    hero: HERO_IMAGES.stockholm,
    headline: "Hur Tassträff i Stockholm sparar 18 timmar i veckan med Hundify",
    summary:
      "Tassträff drev ett av Stockholms äldsta hundpensionat med pappersbokningar och fyra olika Excel-ark. Sedan de bytte till Hundify har Maria och hennes team eliminerat överbokningar helt och vunnit tillbaka 18 timmar i veckan.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Tassträff drev sin verksamhet med en kombination av telefonbokningar, Facebook Messenger och fyra olika Excel-ark som personalen försökte hålla synkroniserade. Resultatet var förutsägbart: dubbel-bokningar, missade dietkrav och en stressad personal som ägnade halva arbetsdagen åt administration istället för åt hundarna.",
        "När antalet rum växte från 18 till 32 blev systemet ohållbart. Maria visste att hon antingen behövde anställa en heltidsadministratör eller hitta ett affärssystem som faktiskt var byggt för hundpensionat.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Tassträff valde Hundify för dess kombination av live beläggningskarta, digitala hundprofiler och automatisk fakturering. Hela onboardingen tog 5 dagar inklusive import av befintliga bokningar och hundprofiler från Excel.",
        "Den digitala incheckningen med signatur på platta blev snabbt det enskilt mest uppskattade nya inslaget — både av personal och av Stockholmskunder som förväntar sig en modern upplevelse.",
      ],
      bullets: [
        "Live beläggningskarta som låser dubbel-bokningar i sekunden",
        "Digitala hundprofiler med kost, mediciner och vaccinationsintyg",
        "Automatisk månadsfakturering via autogiro",
        "Ägar-app med bildflöde och dagliga uppdateringar",
      ],
    },
    results: [
      { value: "18 h", label: "Sparad admintid / vecka" },
      { value: "0", label: "Överbokningar senaste 12 mån" },
      { value: "+24%", label: "Återkommande bokningar" },
    ],
    quote: {
      text:
        "Vi driver hundpensionat i Stockholm sedan 2012. Sedan vi bytte till Hundify har vi sparat 18 timmar i veckan på admin — och den digitala check-in:en är en wow-faktor för våra Stockholmskunder.",
    },
    seoDescription:
      "Kundberättelse: Tassträff Hundpensionat i Bromma, Stockholm sparar 18 timmar i veckan och har noll överbokningar sedan de bytte till Hundifys affärssystem.",
  },

  "vovvevillan-goteborg": {
    slug: "vovvevillan-goteborg",
    citySlug: "goteborg",
    business: "Vovvevillan Hunddagis",
    city: "Göteborg",
    area: "Majorna, Göteborg",
    industry: "Hunddagis",
    size: "120 hundar i veckan · 9 anställda",
    customerSince: "Kund sedan 2024",
    person: { name: "Jonas Eriksson", role: "VD", initial: "J" },
    modules: ["Hunddagis App", "Ägar-app", "Stripe-integration"],
    hero: HERO_IMAGES.goteborg,
    headline: "Vovvevillan Göteborg: från Swish-jakt till 97% betalda fakturor",
    summary:
      "Innan Hundify drev Vovvevillan månadsfaktureringen för hand och jagade Swish-betalningar varje vecka. Nu går 97% av fakturorna in på förfallodagen och Jonas har vunnit tillbaka helgerna.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Vovvevillan är ett av Majornas mest etablerade hunddagis med 120 hundar i veckan. Tillväxten var en välsignelse — men också ett problem. Månadsfaktureringen tog Jonas och hans assistent 14 timmar varje månad, och ändå låg ~22% av fakturorna obetalda vid förfallodag.",
        "Vovvevillan provade tre olika svenska bokningssystem innan de hittade Hundify. Inget var byggt specifikt för svenska hunddagis — och inget integrerade rent mot Stripe och autogiro.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Vovvevillan rullade ut Hundifys hunddagis-modul med automatisk månadsfakturering via Stripe och autogiro. Bildflödet och tvåvägs-chatten i ägar-appen blev snabbt en konkurrensfördel jämfört med andra dagis i Göteborg.",
        "Bookingsändringar som tidigare drog personalen från golvet hanteras nu av kunderna själva i appen — utan att intäkten påverkas.",
      ],
      bullets: [
        "Återkommande månadsfakturering via Stripe + autogiro",
        "Ägar-app med push-notiser och bildflöde",
        "Närvarorapport med ett klick vid grinden",
        "Automatisk SIE-export till revisorn",
      ],
    },
    results: [
      { value: "97%", label: "Fakturor betalda i tid" },
      { value: "14 → 1 h", label: "Tid för månadsfakturering" },
      { value: "+18%", label: "Nettomarginal år 1" },
    ],
    quote: {
      text:
        "Som hunddagis i Göteborg har vi länge letat efter ett affärssystem som hanterar både bokningar, fakturor och kundkommunikation utan tre olika appar. Hundify levererar — och kundsupporten svarar på svenska.",
    },
    seoDescription:
      "Kundberättelse: Vovvevillan Hunddagis i Majorna, Göteborg ökade andelen betalda fakturor från 78% till 97% och kapade månadsadministrationen från 14h till 1h med Hundify.",
  },

  "skanepatrullen-malmo": {
    slug: "skanepatrullen-malmo",
    citySlug: "malmo",
    business: "Skånepatrullen Hunddagis",
    city: "Malmö",
    area: "Limhamn, Malmö",
    industry: "Hunddagis",
    size: "85 hundar i veckan · 6 anställda",
    customerSince: "Kund sedan 2024",
    person: { name: "Lina Pettersson", role: "Ägare", initial: "L" },
    modules: ["Hunddagis App", "Ägar-app", "Fakturering"],
    hero: HERO_IMAGES.malmo,
    headline: "Skånepatrullen i Malmö: dubblade NPS och vann sin första 5-stjärniga recension",
    summary:
      "Med danska och svenska kunder i Öresundsregionen behövde Skånepatrullen ett system som klarar både SEK och DKK samt levererar en ägar-upplevelse i världsklass. Hundify gav dem båda.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Skånepatrullen är ett av Limhamns mest älskade hunddagis, men kundnöjdheten började stagnera. Lina visste att problemet inte var omsorgen — den var i världsklass — utan kommunikationen. Föräldrar fick bilder via tre olika kanaler och fakturor i fel valuta.",
        "Med en växande dansk kundbas behövdes också flexibel valutahantering och flerspråkig ägar-app.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Hundify rullades ut med fokus på ägar-appen och automatiserad bildkommunikation. Varje hund får sin egen tidslinje där föräldrar ser bilder, dagsanteckningar och fakturor — på svenska eller danska.",
        "Faktureringsmodulen hanterar både SEK och DKK utan att personalen behöver tänka på växelkurser.",
      ],
      bullets: [
        "Ägar-app på svenska och danska",
        "Flerkanals bildflöde med automatisk taggning",
        "Fakturor i SEK eller DKK per kund",
        "Närvarorapport med ett klick",
      ],
    },
    results: [
      { value: "NPS 38 → 71", label: "Net Promoter Score" },
      { value: "+31%", label: "Återkommande kunder" },
      { value: "4.9 ⭐", label: "Genomsnittlig Google-recension" },
    ],
    quote: {
      text:
        "Bokningssystem, fakturor och ägar-app — allt på svenska och perfekt för oss som driver hunddagis i Malmö. Våra kunder älskar bildflödet, och vi älskar att fakturorna går ut automatiskt.",
    },
    seoDescription:
      "Kundberättelse: Skånepatrullen Hunddagis i Limhamn, Malmö dubblade sitt NPS och vann sin första 5-stjärniga Google-recension efter att ha bytt till Hundify.",
  },

  "uppsala-hundhotell": {
    slug: "uppsala-hundhotell",
    citySlug: "uppsala",
    business: "Uppsala Hundhotell",
    city: "Uppsala",
    area: "Gränby, Uppsala",
    industry: "Hundpensionat",
    size: "40 rum · 11 anställda",
    customerSince: "Kund sedan 2022",
    person: { name: "Anders Bergström", role: "Grundare", initial: "A" },
    modules: ["Hundpensionat CMS", "Hunddagis App", "API-integrationer"],
    hero: HERO_IMAGES.uppsala,
    headline: "Uppsala Hundhotell skalade från 22 till 40 rum — utan att anställa en enda administratör",
    summary:
      "Anders Bergström expanderade Uppsalas största hundpensionat med 82% utan att lägga till en enda administrativ tjänst. Hundify var det skalbara fundamentet bakom tillväxten.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Uppsala Hundhotell drev 22 rum med en stab på 6. Bokningar kom från ett gammalt PHP-system som tre olika konsulter hade lappat ihop genom åren. Varje större förändring krävde kod och tid Anders inte hade.",
        "När en utökning till 40 rum planerades stod det klart att den befintliga lösningen inte skulle skala — och att en ny administrativ heltidstjänst skulle äta upp marginalen.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Hundify ersatte hela det gamla systemet, inklusive bokning, journal och fakturering. API-integrationerna mot Fortnox och Klarna sattes upp på en eftermiddag.",
        "Kombinationen av live beläggningskarta, automatiska kundbekräftelser och digital incheckning gjorde att teamet kunde dubbla kapaciteten utan att lägga till en enda administrativ tjänst.",
      ],
      bullets: [
        "Skalbart bokningssystem som hanterar 40+ rum",
        "API-integrationer mot Fortnox och Klarna",
        "Automatisk kundkommunikation (mail + SMS)",
        "Realtids-rapporter för beläggning och prognos",
      ],
    },
    results: [
      { value: "22 → 40", label: "Rum (82% tillväxt)" },
      { value: "0", label: "Nya administrativa tjänster" },
      { value: "94%", label: "Beläggning under högsäsong" },
    ],
    quote: {
      text:
        "Vi driver Uppsalas största hundpensionat med 40 rum. Sedan vi bytte till Hundifys affärssystem har vi noll överbokningar och betydligt nöjdare personal — pappersarbetet är borta.",
    },
    seoDescription:
      "Kundberättelse: Uppsala Hundhotell skalade från 22 till 40 rum (82% tillväxt) utan att anställa en enda administratör — tack vare Hundifys affärssystem för hundpensionat.",
  },

  "malardalens-hunddagis-vasteras": {
    slug: "malardalens-hunddagis-vasteras",
    citySlug: "vasteras",
    business: "Mälardalens Hunddagis",
    city: "Västerås",
    area: "Vallby, Västerås",
    industry: "Hunddagis",
    size: "55 hundar i veckan · 4 anställda",
    customerSince: "Kund sedan 2024",
    person: { name: "Petra Holm", role: "Ägare", initial: "P" },
    modules: ["Hunddagis App", "Ägar-app"],
    hero: HERO_IMAGES.vasteras,
    headline: "Mälardalens Hunddagis i Västerås halverade kundförfrågningarna — utan att tappa kundnöjdhet",
    summary:
      "Genom att flytta löpande kundkommunikation in i Hundifys ägar-app halverade Mälardalens Hunddagis antalet inkommande mail och SMS — utan att tappa en enda kundnöjdhetspoäng.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Petra och hennes team spenderade kvällar med att svara på samma frågor om och om igen: när hämtas Bossen, behövs ny vaccination, kan vi få bilder från idag? Mailen kom i alla kanaler.",
        "Konsekvensen var burnout och en kommunikation som ofta nådde fram för sent.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Hundify rullades ut med fullt fokus på ägar-appen. Föräldrar fick svar på sina vanligaste frågor direkt i appen — vaccinationsstatus, dagens schema, bilder, fakturor.",
        "Stödet för push-notiser gjorde att Mälardalens slutade jaga svar via mail.",
      ],
      bullets: [
        "Ägar-app med all info i en flik",
        "Push-notiser istället för mail/SMS",
        "Automatiska vaccinationspåminnelser",
        "Tvåvägs-chat per hund",
      ],
    },
    results: [
      { value: "−52%", label: "Inkommande kundmail" },
      { value: "+0.3 NPS", label: "Kundnöjdhet (trots minskad kontakt)" },
      { value: "9 h", label: "Sparad tid / vecka" },
    ],
    quote: {
      text:
        "Hundify fungerar lika bra som affärssystem för vårt hunddagis i Västerås som det skulle göra i Stockholm. Att kunna boka av-och-på platser via ägar-appen sparar oss flera timmar varje vecka.",
    },
    seoDescription:
      "Kundberättelse: Mälardalens Hunddagis i Västerås halverade inkommande kundmail med Hundifys ägar-app — utan att tappa en enda kundnöjdhetspoäng.",
  },

  "narkes-hundvanner-orebro": {
    slug: "narkes-hundvanner-orebro",
    citySlug: "orebro",
    business: "Närkes Hundvänner",
    city: "Örebro",
    area: "Adolfsberg, Örebro",
    industry: "Hundrastning",
    size: "80 promenader / vecka · 5 hundvakter",
    customerSince: "Kund sedan 2024",
    person: { name: "Henrik Strand", role: "VD", initial: "H" },
    modules: ["Hundrastning & Schema", "GPS-spårning", "Pass-rapporter"],
    hero: HERO_IMAGES.orebro,
    headline: "Närkes Hundvänner i Örebro: från Excel-schema till GPS-spårning som kunderna älskar",
    summary:
      "Henrik drev en blomstrande hundrastningstjänst i Örebro — men personalschema och rapporteringen tog mer tid än själva promenaderna. Hundify löste det.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Med fem hundvakter och 80 promenader i veckan blev personalschemaläggningen ett gissel. Henrik byggde scheman i Excel söndagskvällar och justerade dem manuellt när någon blev sjuk. Kunder bad om bevis på att promenaderna hade skett — men det enda Henrik kunde erbjuda var en signatur på papper.",
        "Närkes Hundvänner riskerade att tappa kunder till nyare, mer tech-orienterade konkurrenter.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Hundify Hundrastning-modulen löste både schemaläggning och rapportering på en gång. Hundvakterna klockar in via appen, GPS:en spårar varje promenad och en pass-rapport (rutt, distans, bilder, anteckningar) genereras automatiskt till ägaren.",
        "Kundbasens reaktion var omedelbar.",
      ],
      bullets: [
        "Drag-and-drop personal-schema",
        "Live GPS-spår synligt för ägaren i appen",
        "Automatiska pass-rapporter efter varje promenad",
        "Klocka in/ut för automatisk löneunderlag",
      ],
    },
    results: [
      { value: "100%", label: "Promenader med GPS-spår" },
      { value: "+27%", label: "Nya kunder första kvartalet" },
      { value: "0", label: "Missade pass på 6 månader" },
    ],
    quote: {
      text:
        "Vi rastar 80 hundar i veckan runt Örebro. GPS-spårningen i Hundify ger våra kunder den trygghet de förväntar sig — och våra hundvakter slipper jobba i tre olika appar samtidigt.",
    },
    seoDescription:
      "Kundberättelse: Närkes Hundvänner i Örebro digitaliserade personal-schema och pass-rapportering med Hundifys GPS-spårning — och vann 27% fler kunder första kvartalet.",
  },

  "ostgota-hundtjanst-linkoping": {
    slug: "ostgota-hundtjanst-linkoping",
    citySlug: "linkoping",
    business: "Östgöta Hundtjänst",
    city: "Linköping",
    area: "Berga, Linköping",
    industry: "Kombinerad verksamhet (Dagis + Pensionat + Promenader)",
    size: "70 hundar i veckan · 8 anställda",
    customerSince: "Kund sedan 2023",
    person: { name: "Cecilia Norén", role: "Grundare", initial: "C" },
    modules: ["Alla moduler", "API", "White-label"],
    hero: HERO_IMAGES.linkoping,
    headline: "Östgöta Hundtjänst körde igång alla tre Hundify-moduler på en vecka",
    summary:
      "Cecilia ville bygga Linköpings första riktiga all-in-one-hundverksamhet — dagis, pensionat och promenader under ett tak. Hundify gav henne fundamentet på sju dagar.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Östgöta Hundtjänst sjösattes som en hybridverksamhet: dagis dagtid, pensionat över helgerna, hundrastning för stamkunder mitt i veckan. Den affärsmodellen krävde ett system som hanterar alla tre — och inget av de befintliga svenska systemen gjorde det.",
        "Cecilia ville inte starta med tre olika verktyg och försöka få dem att prata.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Hundify rullade ut alla tre moduler (Hundpensionat CMS, Hunddagis App, Hundrastning & Schema) på sju dagar — inklusive white-label av ägar-appen med Östgötas logga och färger.",
        "API-integrationen mot Fortnox sköter all bokföring automatiskt.",
      ],
      bullets: [
        "Alla tre moduler i ett system",
        "White-label ägar-app",
        "API-integration mot Fortnox",
        "Gemensam kundregister över alla tjänster",
      ],
    },
    results: [
      { value: "7 dagar", label: "Från onboarding till skarp drift" },
      { value: "3-i-1", label: "Moduler i samma system" },
      { value: "+42%", label: "Omsättning år 1" },
    ],
    quote: {
      text:
        "Som Linköpings ledande hundverksamhet behövde vi ett modernt system som klarar både dagis, pensionat och promenader. Hundify var rätt val från dag ett — vi hade igång det inom en vecka.",
    },
    seoDescription:
      "Kundberättelse: Östgöta Hundtjänst i Linköping körde igång dagis, pensionat och hundrastning i samma Hundify-instans på sju dagar — och växte 42% första året.",
  },

  "sundets-hundhotell-helsingborg": {
    slug: "sundets-hundhotell-helsingborg",
    citySlug: "helsingborg",
    business: "Sundets Hundhotell",
    city: "Helsingborg",
    area: "Råå, Helsingborg",
    industry: "Hundpensionat",
    size: "24 rum · 7 anställda",
    customerSince: "Kund sedan 2023",
    person: { name: "Markus Lundgren", role: "Ägare", initial: "M" },
    modules: ["Hundpensionat CMS", "Flerspråkig ägar-app", "Flervalutafakturering"],
    hero: HERO_IMAGES.helsingborg,
    headline: "Sundets Hundhotell i Helsingborg vann den danska Öresundskunden med flerspråkig ägar-app",
    summary:
      "Med dansk kundbas på andra sidan sundet behövde Sundets Hundhotell hantera två språk och två valutor — utan dubbelarbete. Hundify löste det elegant.",
    challenge: {
      title: "Utmaningen",
      paragraphs: [
        "Sundets Hundhotell ligger 15 minuter med färja från Köpenhamn. Var fjärde gäst kom från Danmark, men deras tidigare system pratade bara svenska och fakturerade bara i SEK.",
        "Markus förlorade danska gäster till köpenhamnska pensionat som hade en mer naturlig digital upplevelse.",
      ],
    },
    solution: {
      title: "Lösningen",
      paragraphs: [
        "Hundify Pro rullades ut med flerspråkig ägar-app (svenska + danska) och flervalutafakturering. Markus' danska kunder ser priser i DKK, får fakturor i DKK och betalar via Mobilepay.",
        "Den lokala fördelen försvann snabbt för konkurrenterna.",
      ],
      bullets: [
        "Flerspråkig ägar-app (svenska + danska)",
        "Fakturering i SEK eller DKK per kund",
        "Mobilepay-integration",
        "Vaccinationsregler enligt både svenska och danska krav",
      ],
    },
    results: [
      { value: "+58%", label: "Danska gäster år 1" },
      { value: "92%", label: "Beläggning över sommaren" },
      { value: "4.8 ⭐", label: "Genomsnittlig recension" },
    ],
    quote: {
      text:
        "Vi har gäster från både Sverige och Danmark här i Helsingborg. Hundifys flerspråkiga ägar-app gör vardagen smidig, och vi kan skicka fakturor i SEK eller DKK utan att tänka på det.",
    },
    seoDescription:
      "Kundberättelse: Sundets Hundhotell i Råå, Helsingborg ökade danska gäster med 58% år 1 tack vare Hundifys flerspråkiga ägar-app och flervalutafakturering.",
  },
};

export const CASE_STUDY_SLUGS = Object.keys(CASE_STUDIES);

export function resolveCaseStudy(slug) {
  if (!slug) return null;
  const decoded = decodeURIComponent(slug).toLowerCase().trim();
  return CASE_STUDIES[decoded] || null;
}

export function getCaseStudyByCity(citySlug) {
  if (!citySlug) return null;
  return Object.values(CASE_STUDIES).find((c) => c.citySlug === citySlug) || null;
}
